import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { Task } from "@common/types/task";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { TimePicker } from "@design-system/TimePicker/TimePicker";
import { formatDuration } from "@design-system/TimePicker/TimePicker.lib";
import { useUpdateTask } from "@api/task.api";
import { EditableCellContent } from "@design-system/ViewTable/EditableCellContent";
import { memo } from "react";
import { CaptureClicks } from "@design-system/CaptureClicks/CaptureClicks";
import { NestedTaskRow } from "../../types";
import { GroupEstimateSummary } from "./TaskGroupSummaryCells";

const TaskEstimateCell = memo(
    ({ row }: CellContext<ViewTableFeatures, NestedTaskRow, number | null>) => {
        const task = row.original as Task;
        const updateTask = useUpdateTask(task);

        if (row.original.isNewPlaceholder) {
            return null;
        }

        const formattedValue = formatDuration(task.estimatedTime ?? undefined);

        return (
            <ViewTableCell>
                <div>
                    <CaptureClicks>
                        <TimePicker
                            hideIcon
                            value={task.estimatedTime ?? undefined}
                            onChange={(value) =>
                                updateTask.mutate({
                                    estimatedTime: value ?? null,
                                })
                            }
                            trigger={
                                <EditableCellContent
                                    isEmpty={!task.estimatedTime}
                                    addText="Add estimate"
                                    hideIcon
                                >
                                    {formattedValue}
                                </EditableCellContent>
                            }
                        />
                    </CaptureClicks>
                </div>
            </ViewTableCell>
        );
    },
);

export const EstimateCell = (
    props: CellContext<ViewTableFeatures, NestedTaskRow, number | null>,
) =>
    props.row.original.isGroup ? (
        <GroupEstimateSummary {...props} />
    ) : (
        <TaskEstimateCell {...props} />
    );
